/**
 * Flash Sale Status Hook
 *
 * Provides flash sale status text for cart items
 * Shows start/end time for flash sale products
 *
 * @module @kidslabs/module-flash-sale/modules/quote/hooks
 */

import { useEffect, useMemo } from 'react';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'next-i18next';
import { useFlashSaleProductsList, useFlashSaleSessions } from '../../../api/requests';
import { extractTimeFromISOString, isToday } from '../../../utils/helpers';
import { cartStoreStateAPI } from '@kidslabs/module-quote';

export const useFlashSaleStatus = (sku?: string, quantity?: number) => {
    const { t } = useTranslation('flash-sale');

    const { salesData } = useFlashSaleProductsList(sku ?? '');
    const { data: sessions } = useFlashSaleSessions();

    const flashSaleStock = useMemo(() => {
        const dealQty = salesData?.deal_qty ?? 0;
        const saleQty = salesData?.sale_qty ?? 0;
        return dealQty - saleQty;
    }, [salesData]);

    const limitPerOrder = salesData?.limit_per_order ?? null;

    const matchedSession = useMemo(
        () => sessions?.find(s => s?.flashsale_id === salesData?.flashsale_id),
        [sessions, salesData?.flashsale_id]
    );

    const statusText = useMemo(() => {
        if (flashSaleStock <= 0) {
            if (matchedSession?.flashsale_type === 'running') return t('text_flash_sale_out_of_stock');
            return;
        }

        const totalQty = quantity ?? 0;
        const type = matchedSession?.flashsale_type ?? '';
        const dateFrom = matchedSession?.date_from ?? '';
        const dateTo = matchedSession?.date_to ?? '';

        if (limitPerOrder != null && totalQty > limitPerOrder) {
            return t('text_flash_sale_exceed_limit_per_order', {
                limit: limitPerOrder,
            });
        }

        if (totalQty > flashSaleStock) {
            return t('text_flash_sale_exceed_remaining', {
                remaining: flashSaleStock,
            });
        }

        if (type === 'running' && isToday(dateTo)) {
            return `${t('text_flash_sale_end')} ${extractTimeFromISOString(dateTo)}`;
        }

        if (type === 'upcoming' && isToday(dateFrom)) {
            return `${t('text_flash_sale_start')} ${extractTimeFromISOString(dateFrom)}`;
        }

        return;
    }, [
        matchedSession?.flashsale_type,
        matchedSession?.date_from,
        matchedSession?.date_to,
        t,
        flashSaleStock,
        quantity,
        limitPerOrder,
    ]);

    const isDisabled = useMemo(() => {
        const totalQty = quantity ?? 0;
        if (!salesData?.flashsale_id) return false;
        if (limitPerOrder != null && totalQty > limitPerOrder) return true;
        if (flashSaleStock <= 0) return true;
        if (matchedSession?.flashsale_type === 'upcoming') return true;
        return false;
    }, [quantity, salesData?.flashsale_id, limitPerOrder, flashSaleStock, matchedSession?.flashsale_type]);

    const adjustableQuantity = useMemo<number | null>(() => {
        if (!salesData?.flashsale_id) return null;
        if (flashSaleStock <= 0) return null;
        if (matchedSession?.flashsale_type === 'upcoming') return null;

        const ceiling = Math.min(flashSaleStock, limitPerOrder ?? flashSaleStock);
        if (ceiling <= 0) return null;
        if ((quantity ?? 0) <= ceiling) return null;

        return ceiling;
    }, [salesData?.flashsale_id, flashSaleStock, matchedSession?.flashsale_type, limitPerOrder, quantity]);

    const storedIsSalable = useSelector(() =>
        sku ? cartStoreStateAPI.getAvailableItemsObservable().get()?.[sku]?.isSalable : undefined
    );

    useEffect(() => {
        if (!sku || !salesData?.flashsale_id) return;
        const flashSaleBlocks = isDisabled || adjustableQuantity !== null;
        const targetIsSalable = flashSaleBlocks ? false : (storedIsSalable ?? true);
        if (storedIsSalable === targetIsSalable) return;
        const current = cartStoreStateAPI.getAvailableItemsObservable().get();
        const existing = current[sku];
        cartStoreStateAPI.setAvailableItems({
            ...current,
            [sku]: {
                isSalable: targetIsSalable,
                loading: existing?.loading ?? false,
                availableQuantity: adjustableQuantity ?? existing?.availableQuantity ?? null,
                errors: statusText ? [{ code: '', message: statusText }] : null,
            },
        });
    }, [sku, isDisabled, adjustableQuantity, statusText, salesData?.flashsale_id, storedIsSalable]);

    return { statusText, isDisabled, adjustableQuantity };
};
